import {
  ApplicationUpdateHistory,
  CreditApplication,
  CreditApplicationFeedbackStatus,
  formatMoney,
  getFeedbackStatus,
  InternalFormSubmission,
} from '@dill/dill-shared';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowCounterClockwise,
  ChatText,
  DotsThreeVertical,
  Flag,
  MapPin,
  PaperPlaneTilt,
  Pencil,
  SlidersHorizontal,
  Trash,
  UserCirclePlus,
  X,
} from 'phosphor-react';
import { Eye } from 'react-feather';
import moment from 'moment';
import { COLORS } from '../../../../utils/colors';
import AppCreditApplicationStatus from './CreditApplicationStatus';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getCreditApplicationPDF,
  getCreditApplications,
  updateApplicationUpdateHistory,
  updateSupplierCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import {
  getInternalFormSubmissionPDF,
  resendInternalFormEmail,
  deleteInternalFormSubmission,
  updateInternalFormSubmissionDetails,
} from '../../../../redux/services/internalFormsService';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import { openDillToast, buildSupplierPath, formatBranchLabel } from '../../../../utils/helpers';
import { AppPdfPreviewModal } from '../../../general/AppPdfPreviewModal/AppPdfPreviewModal';

import RequestApplicationUpdateModal from '../CreditApplicationComponents/RequestApplicationUpdateModal';
import AssignBranchModal from '../CreditApplicationComponents/AssignBranchModal';
import CreditAppCommentModal from '../CreditApplicationComponents/CreditAppCommentModal';
import ChangeDraftRequirementsModal from '../CreditApplicationComponents/ChangeDraftRequirementsModal';
import SendInternalSignatureDocumentsModal from '../CreditApplicationComponents/SendInternalSignatureDocumentsModal';
import CreditAppNotes from '../CreditApplicationComponents/CreditAppNotes';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { TrayArrowUp } from '@phosphor-icons/react/dist/ssr';
import { TrayArrowDown, DownloadSimple, ChatCircleText } from '@phosphor-icons/react';
import { DillColumn, DillSummaryCard, DillTable } from '../../../general/Dill/DillTable';
import { DillAvatar, DillButton, DillDropdownButton } from '../../../general/Dill';
import { DillDropdown } from '../../../general/Dill/DillDropdown';
import { APP_ENVIRONMENT } from '../../../../utils/constants';
import {
  WithInternalFormSubmission,
  getInternalFormCell,
  getInternalFormSearchValue,
  getInternalFormSortValue,
} from '../InternalForms/internalFormTableRows';

type CreditApplicationWithHistory = CreditApplication &
  WithInternalFormSubmission & {
    applicationUpdateHistoryId: string | null;
    applicationUpdateHistory?: ApplicationUpdateHistory[] | null;
  };

type InternalFormSubmissionRow = Omit<InternalFormSubmission, 'accessToken'>;

const NotesModal = ({
  creditApp,
  isSalesRole,
  onClose,
  onInternalFormUpdated,
}: {
  creditApp: CreditApplicationWithHistory;
  isSalesRole: boolean;
  onClose: () => void;
  onInternalFormUpdated?: (submission: InternalFormSubmissionRow) => void;
}) => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const notes = creditApp.internalNotes ?? [];
  // An internal form row's notes live on the submission, not on a credit application.
  const internalFormSubmission = creditApp.internalFormSubmission;
  const saveInternalFormNotes = internalFormSubmission
    ? async (internalNotes: InternalFormSubmission['internalNotes']) => {
        const result = await dispatch(
          updateInternalFormSubmissionDetails({
            supplierId: currentSupplier?.id ?? '',
            internalFormSubmissionId: internalFormSubmission.id,
            internalNotes,
          })
        );
        if (!result.type.includes('fulfilled')) return false;
        const updated: InternalFormSubmissionRow | null = (result as any).payload?.data ?? null;
        if (updated) onInternalFormUpdated?.(updated);
        return true;
      }
    : undefined;
  const visibleNotes = isSalesRole ? notes.filter((n) => n.visibleToSales || n.fromSales) : notes;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="WHITE-BG rounded-xl shadow-xl w-[480px] max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">Internal Notes</span>
            {visibleNotes.length > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold">
                {visibleNotes.length}
              </span>
            )}
          </div>
          <button onClick={onClose}>
            <X size={18} color={COLORS.GREY_500} />
          </button>
        </div>
        <div className="flex flex-col gap-2 p-4 overflow-y-auto">
          <CreditAppNotes
            notes={notes}
            creditAppId={creditApp.id}
            isSalesRole={isSalesRole}
            applicationHistoryId={creditApp.applicationUpdateHistoryId}
            onNoteAdded={onClose}
            defaultOpen
            title="Write Note"
            hideHeaderCount
            collapseComposerOnly
            onSaveNotes={saveInternalFormNotes}
          />
        </div>
      </div>
    </div>
  );
};

const SalesRepsTooltip = ({
  reps,
  children,
}: {
  reps: string[];
  children: (props: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    ref: React.RefObject<HTMLElement>;
  }) => React.ReactNode;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHideTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleTriggerMouseEnter = () => {
    clearHideTimeout();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const PANEL_W = 200;
      const left =
        rect.right + PANEL_W + 8 > window.innerWidth ? rect.left - PANEL_W - 4 : rect.right + 8;
      setPosition({ top: rect.top, left });
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    clearHideTimeout();
    timeoutRef.current = setTimeout(() => setIsHovered(false), 150);
  };

  useEffect(() => () => clearHideTimeout(), []);

  return (
    <>
      {children({
        onMouseEnter: handleTriggerMouseEnter,
        onMouseLeave: handleMouseLeave,
        ref: triggerRef,
      })}
      {isHovered &&
        createPortal(
          <div
            className="fixed bg-white border border-gray-200 shadow-lg rounded-xl z-[99999] w-[200px]"
            style={{ top: `${position.top}px`, left: `${position.left}px` }}
            onMouseEnter={clearHideTimeout}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => e.stopPropagation()}>
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Sales Reps
              </p>
            </div>
            <div className="flex flex-col p-2 gap-1 max-h-[200px] overflow-y-auto">
              {reps.map((rep, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50">
                  <div className="h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 text-[10px] font-semibold shrink-0">
                    {rep.charAt(0).toUpperCase()}
                  </div>
                  <p className="text-xs text-gray-800 leading-tight">{rep}</p>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

const FEEDBACK_STATUS_LABELS: Record<CreditApplicationFeedbackStatus, string> = {
  SENT: 'Feedback sent',
  IN_REVIEW: 'Customer updated',
  RESOLVED: 'Feedback resolved',
};

// Extra search terms so "feedback review" / "needs review" find rows waiting on the supplier.
const FEEDBACK_STATUS_SEARCH_TERMS: Record<CreditApplicationFeedbackStatus, string> = {
  SENT: 'feedback sent awaiting customer',
  IN_REVIEW: 'customer updated feedback review needs review',
  RESOLVED: 'feedback resolved',
};

const FEEDBACK_STATUS_PRIORITY: CreditApplicationFeedbackStatus[] = [
  'IN_REVIEW',
  'SENT',
  'RESOLVED',
];

// The feedback the table should surface: the newest one needing review, else the newest one still
// waiting on the customer, else the newest resolved one. Field names are what the customer was
// asked to redo (e.g. the EPA form). Grouped fields are stored under their first item's name, so
// they're resolved back to the group name (e.g. "Contact List").
const getLatestFeedback = (
  row: CreditApplicationWithHistory
): {
  scope: string[];
  message: string;
  status: CreditApplicationFeedbackStatus;
  openCount: number;
} | null => {
  const history = row.approvalStatusHistory ?? [];
  const feedback = history
    .filter((entry) => entry.status === 'FEEDBACK_SENT')
    .map((entry) => ({ entry, status: getFeedbackStatus(entry, history) }))
    .sort((a, b) => new Date(b.entry.date).getTime() - new Date(a.entry.date).getTime());
  const shown = FEEDBACK_STATUS_PRIORITY.map((status) =>
    feedback.find((item) => item.status === status)
  ).find(Boolean);
  if (!shown) return null;
  const names = (shown.entry.fields ?? []).map((field) => {
    const fieldValue = row.fieldValues.find((f) => f.uniqueId === field.uniqueId);
    return fieldValue?.groupName || field.name || fieldValue?.name || field.id;
  });
  return {
    scope: Array.from(new Set(names.filter(Boolean))),
    message: shown.entry.comment?.trim() ?? '',
    status: shown.status,
    openCount: feedback.filter((item) => item.status !== 'RESOLVED').length,
  };
};

const CreditApplicationsTable = ({
  creditApplications,
  archiveCreditAppToogle,
  summaryCards,
  toolbarLeft,
  toolbarRight,
  onInternalFormUpdated,
  onInternalFormDeleted,
}: {
  creditApplications: CreditApplicationWithHistory[];
  archiveCreditAppToogle: boolean;
  summaryCards?: DillSummaryCard[];
  toolbarLeft?: React.ReactNode;
  toolbarRight?: React.ReactNode;
  // Called after an internal form row changes here (e.g. a note), so the page can refresh it.
  onInternalFormUpdated?: (submission: InternalFormSubmissionRow) => void;
  // Called after an internal form row is deleted here, so the page can drop it.
  onInternalFormDeleted?: (internalFormSubmissionId: string) => void;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user, users } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);
  const [previewLoading, setPreviewLoading] = useState<string>('');
  const [loadingType, setLoadingType] = useState<string>('');
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string>('');
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const [openRequestUpdateModal, setOpenRequestUpdateModal] = useState(false);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState<string>('');
  const [currentCreditAppId, setCurrentCreditAppId] = useState<string>('');
  const [assignBranchModalOpen, setAssignBranchModalOpen] = useState(false);
  const [creditAppCommentModalOpen, setCreditAppCommentModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesModalCreditApp, setNotesModalCreditApp] =
    useState<CreditApplicationWithHistory | null>(null);
  const [changeDraftRequirementsModalOpen, setChangeDraftRequirementsModalOpen] = useState(false);
  const [changeDraftRequirementsCreditApp, setChangeDraftRequirementsCreditApp] =
    useState<CreditApplicationWithHistory | null>(null);
  const [resendInternalSignatureModalOpen, setResendInternalSignatureModalOpen] = useState(false);

  // Auto-open request update modal when navigated with triggerRequestUpdate state
  useEffect(() => {
    const state = location.state as { triggerRequestUpdate?: boolean } | null;
    if (
      state?.triggerRequestUpdate &&
      creditApplications.length > 0 &&
      APP_ENVIRONMENT !== 'production'
    ) {
      const firstApp = creditApplications[0];
      setOpenRequestUpdateModal(true);
      setSelectedSenderEmail(firstApp.senderEmail || '');
      setCurrentCreditAppId(firstApp.id);
      // Clear the navigation state so it doesn't re-trigger
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, creditApplications]);

  const salesHierarchy = useMemo(() => {
    const supplier = currentSupplier;
    const userId = user?.id ?? '';
    if (!Array.isArray(user?.roles) || !user?.roles.includes('CREDIT_APP_SALES')) return null;
    const isDirector = (supplier?.salesDirectorIds ?? []).includes(userId);
    const userRegion = supplier?.regions?.find((r) => (r.rsmIds ?? []).includes(userId));
    const userBranch = supplierBranches.find((b) =>
      (b.salesRep ?? []).some((sr) => sr.id === userId)
    );
    return { isDirector, userRegion: userRegion ?? null, userBranch: userBranch ?? null };
  }, [user, supplierBranches]);

  const tmHierarchy = useMemo(() => {
    const userId = user?.id ?? '';
    if (!userId) return null;
    const branches = supplierBranches;
    const tmBranches = branches.filter((b) => (b.tmIds ?? []).includes(userId));
    if (tmBranches.length === 0) return null;
    return { tmBranches };
  }, [user, supplierBranches]);

  // Director: can assign to any branch
  // RSM (userRegion set): can assign to branches in their region
  // TM: can assign sales rep (branch locked)
  // Branch-level sales rep: cannot assign
  const canAssignBranch = useMemo(() => {
    if (!salesHierarchy) return true;
    if (salesHierarchy.isDirector || salesHierarchy.userRegion) return true;
    if (tmHierarchy) return true;
    return false;
  }, [salesHierarchy, tmHierarchy]);

  const assignBranchAllowedIds = useMemo((): string[] | undefined => {
    const isSalesRole = user?.roles?.includes('CREDIT_APP_SALES');
    if (tmHierarchy && isSalesRole) return tmHierarchy.tmBranches.map((b) => b.branchId);
    if (!salesHierarchy) {
      return undefined;
    }
    if (salesHierarchy.isDirector) return undefined;
    if (salesHierarchy.userRegion) return salesHierarchy.userRegion.branchIds ?? [];
    if (tmHierarchy) return tmHierarchy.tmBranches.map((b) => b.branchId);
    return [];
  }, [salesHierarchy, tmHierarchy]);

  const companyUsers = useMemo(() => {
    return users
      .filter((item) => {
        const branches = supplierBranches;
        const salesReps = branches
          .filter((item1) => item1.salesRep)
          .flatMap((branch) => branch.salesRep)
          .map((salesRep) => salesRep?.id ?? '');
        return !salesReps.includes(item.id);
      })
      .map((usr) => {
        const label = `${usr.firstName} ${usr.lastName}`;
        const splitLabel = label.split(' ');
        return {
          id: usr.id,
          label: `${splitLabel[0][0] ?? ''} ${splitLabel[1][0] ?? ''}`,
          name: label,
          email: usr.email,
        };
      });
  }, [users, supplierBranches]);

  const handlePreviewOrDownload = useCallback(
    async (creditApplicationId: string, type: string, isInternalForm = false) => {
      setPreviewLoading(creditApplicationId);
      setLoadingType(type);
      const results = isInternalForm
        ? await dispatch(
            getInternalFormSubmissionPDF({
              internalFormSubmissionId: creditApplicationId,
              preview: type === 'preview',
              supplierId: currentSupplier?.id || '',
            })
          )
        : await dispatch(
            getCreditApplicationPDF({
              creditApplicationId,
              preview: type === 'preview',
              supplierId: currentSupplier?.id || '',
            })
          );
      if (results.type.endsWith('/fulfilled')) {
        const payload = results.payload as { data: { pdfUrl: string } };
        if (type === 'preview') {
          if (!payload?.data?.pdfUrl) {
            openDillToast({ type: 'ERROR', message: 'Failed to fetch PDF' });
            setPreviewLoading('');
            setLoadingType('');
            return;
          } else {
            setCurrentPdfUrl(payload?.data?.pdfUrl);
            setPreviewModalOpen(true);
          }
        }
        setPreviewLoading('');
        setLoadingType('');
      } else {
        openDillToast({ type: 'ERROR', message: 'Failed to fetch PDF' });
        setPreviewLoading('');
        setLoadingType('');
      }
    },
    [dispatch, user]
  );

  const handleUpdateFormBallInCourt = async (
    internalFormSubmissionId: string,
    userId: string | null
  ) => {
    const result = await dispatch(
      updateInternalFormSubmissionDetails({
        supplierId: currentSupplier?.id ?? '',
        internalFormSubmissionId,
        ballInCourtId: userId,
      })
    );
    const updated: InternalFormSubmissionRow | null = result.type.includes('fulfilled')
      ? (result as any).payload?.data ?? null
      : null;
    if (updated) onInternalFormUpdated?.(updated);
    openDillToast(
      updated
        ? { type: 'SUCCESS', message: 'Ball in court updated' }
        : { type: 'ERROR', message: 'Failed to update ball in court' }
    );
  };

  // Internal / external forms: re-sends the link to whoever the form is waiting on.
  const handleResendInternalForm = async (internalFormSubmissionId: string) => {
    if (!currentSupplier?.id) return;
    setPreviewLoading(internalFormSubmissionId);
    const result = await dispatch(
      resendInternalFormEmail({ supplierId: currentSupplier.id, internalFormSubmissionId })
    );
    setPreviewLoading('');
    openDillToast(
      result.type.includes('fulfilled')
        ? { type: 'SUCCESS', message: 'Email resent' }
        : {
            type: 'ERROR',
            message: (result as any).payload || 'Failed to resend the email',
          }
    );
  };

  const handleDeleteInternalForm = async (internalFormSubmissionId: string) => {
    if (!currentSupplier?.id) return;
    setPreviewLoading(internalFormSubmissionId);
    const result = await dispatch(
      deleteInternalFormSubmission({ supplierId: currentSupplier.id, internalFormSubmissionId })
    );
    setPreviewLoading('');
    if (result.type.includes('fulfilled')) {
      onInternalFormDeleted?.(internalFormSubmissionId);
      openDillToast({ type: 'SUCCESS', message: 'Successfully deleted form' });
    } else {
      openDillToast({
        type: 'ERROR',
        message: (result as any).payload || 'Failed to delete form',
      });
    }
  };

  const handleDeleteCreditApplication = async (id: string) => {
    let results = null;
    const application = creditApplications.find((app) => app.id === id);
    if (application?.applicationUpdateHistoryId) {
      results = await dispatch(
        updateApplicationUpdateHistory({
          creditApplicationId: id,
          applicationHistoryId: application?.applicationUpdateHistoryId,
          supplierId: currentSupplier?.id || '',
          isDeleted: true,
        })
      );
    } else {
      results = await dispatch(
        updateSupplierCreditApplication({
          creditApplicationId: id,
          supplierId: currentSupplier?.id || '',
          isDeleted: true,
        })
      );
    }
    if (results && results.type.includes('fulfilled')) {
      openDillToast({ type: 'SUCCESS', message: 'Successfully deleted application' });
      await dispatch(getCreditApplications({ supplierId: currentSupplier?.id ?? '' }));
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to delete application' });
    }
  };

  const handleArchiveOfUnarchiveCreditApplication = useCallback(
    async (id: string, val: string) => {
      let results = null;
      const application = creditApplications.find((app) => app.id === id);
      if (application?.applicationUpdateHistoryId) {
        results = await dispatch(
          updateApplicationUpdateHistory({
            creditApplicationId: id,
            applicationHistoryId: application?.applicationUpdateHistoryId,
            supplierId: currentSupplier?.id || '',
            isArchived: val === 'archive',
          })
        );
      } else {
        results = await dispatch(
          updateSupplierCreditApplication({
            creditApplicationId: id,
            supplierId: currentSupplier?.id || '',
            isArchived: val === 'archive',
          })
        );
      }
      if (results && results.type.includes('fulfilled')) {
        openDillToast({
          type: 'SUCCESS',
          message: `Successfully ${val === 'archive' ? 'archived' : 'unarchived'} application`,
        });
        await dispatch(getCreditApplications({ supplierId: currentSupplier?.id ?? '' }));
      } else {
        openDillToast({
          type: 'ERROR',
          message: `Failed to ${val === 'archive' ? 'archive' : 'unarchive'} application`,
        });
      }
    },
    [dispatch, creditApplications, user]
  );

  // The Ball in Court column's assignee picker — for credit applications and internal / external
  // forms alike.
  const renderBallInCourt = (
    ballInCourtId: string | null | undefined,
    onChange: (userId: string | null) => void
  ) => {
    const isSalesRole = user?.roles?.includes('CREDIT_APP_SALES') ?? false;
    const assigned = companyUsers.find((u) => u.id === ballInCourtId);
    const assignedIdx = assigned ? companyUsers.findIndex((u) => u.id === assigned.id) : 0;
    const items = [
      {
        label: 'Unassigned',
        value: '',
        labelInElement: (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full border border-dashed border-gray-300 flex items-center justify-center shrink-0">
              <span className="text-[9px] text-gray-400">—</span>
            </div>
            <span className="text-xs text-gray-400 italic">Unassigned</span>
          </div>
        ),
      },
      ...companyUsers.map((u, idx) => ({
        label: u.name,
        value: u.id,
        labelInElement: (
          <div className="flex items-center gap-2">
            <DillAvatar name={u.name} size={24} idx={idx} />
            <div className="flex flex-col min-w-0">
              <span className="text-xs text-gray-900 truncate">{u.name}</span>
              <span className="text-[10px] text-gray-400 truncate">{u.email}</span>
            </div>
          </div>
        ),
      })),
    ];
    return (
      <div className="w-full" onClick={(e) => e.stopPropagation()}>
        <DillDropdown
          items={items}
          itemWidth={220}
          value={ballInCourtId ?? ''}
          disabled={isSalesRole}
          isSearchable={false}
          placeholder="Assign user"
          containerClassName={`h-8 py-0 ${
            !assigned ? 'border-dashed border-gray-300' : 'border-gray-200'
          }`}
          customViewElement={
            assigned ? (
              <div className="flex items-center gap-2 px-2 h-full w-full overflow-hidden">
                <DillAvatar name={assigned.name} size={20} idx={assignedIdx} />
                <span className="text-xs text-gray-800 truncate flex-1 min-w-0">
                  {assigned.name}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-2 h-full w-full">
                <UserCirclePlus size={16} className="text-gray-400 shrink-0" />
                <span className="text-xs text-gray-400 truncate">Assign user</span>
              </div>
            )
          }
          onSelectChange={(opt) => onChange(opt.value || null)}
        />
      </div>
    );
  };

  const handleUpdateBallInCourt = useCallback(
    async (creditApplicationId: string, userId: string | null) => {
      const results = await dispatch(
        updateSupplierCreditApplication({
          creditApplicationId,
          supplierId: currentSupplier?.id || '',
          ballInCourtId: userId,
        })
      );
      if (results && results.type.includes('fulfilled')) {
        openDillToast({ type: 'SUCCESS', message: 'Ball in court updated' });
      } else {
        openDillToast({ type: 'ERROR', message: 'Failed to update ball in court' });
      }
    },
    [dispatch, currentSupplier?.id]
  );

  const handleSelectValue = useCallback(
    (val: string, id: string) => {
      if (val === 'preview') handlePreviewOrDownload(id, 'preview');
      if (val === 'download') handlePreviewOrDownload(id, 'download');
      if (val === 'request-update') {
        setOpenRequestUpdateModal(true);
        const creditApplication = creditApplications.find((app) => app.id === id);
        setSelectedSenderEmail(creditApplication?.senderEmail || '');
        setCurrentCreditAppId(id);
      }
      if (val === 'delete') handleDeleteCreditApplication(id);
      if (val === 'resend') {
        setCurrentCreditAppId(id);
        setCreditAppCommentModalOpen(true);
      }
      if (val === 'change-requirements') {
        const creditApp = creditApplications.find((app) => app.id === id) ?? null;
        setChangeDraftRequirementsCreditApp(creditApp);
        setChangeDraftRequirementsModalOpen(true);
      }
      if (val === 'resend-internal-signature') {
        setCurrentCreditAppId(id);
        setResendInternalSignatureModalOpen(true);
      }
      if (val === 'archive' || val === 'unarchive')
        handleArchiveOfUnarchiveCreditApplication(id, val);
    },
    [
      handlePreviewOrDownload,
      creditApplications,
      handleDeleteCreditApplication,
      handleArchiveOfUnarchiveCreditApplication,
    ]
  );

  const columns = useMemo<DillColumn<CreditApplicationWithHistory>[]>(
    () => [
      {
        key: 'contact',
        label: 'Contact',
        width: 180,
        sortable: true,
        sticky: true,
        getSortValue: (row: CreditApplicationWithHistory) => {
          const firstName =
            row.fieldValues.find((f) => f.id.includes('first') && f.stepNumber === 0)?.value ?? '';
          const lastName =
            row.fieldValues.find((f) => f.id.includes('last') && f.stepNumber === 0)?.value ?? '';
          return `${firstName} ${lastName}`.trim().toLowerCase();
        },
        getSearchValue: (row: CreditApplicationWithHistory) => {
          const firstName =
            row.fieldValues.find((f) => f.id.includes('first') && f.stepNumber === 0)?.value ?? '';
          const lastName =
            row.fieldValues.find((f) => f.id.includes('last') && f.stepNumber === 0)?.value ?? '';
          return `${firstName} ${lastName} ${row?.senderEmail ?? ''}`.replace(/\s+/g, ' ').trim();
        },
        render: (_: any, row: CreditApplicationWithHistory) => {
          const firstName = row.fieldValues.find(
            (f) => f.id.includes('first') && f.stepNumber === 0
          )?.value;
          const lastName = row.fieldValues.find(
            (f) => f.id.includes('last') && f.stepNumber === 0
          )?.value;
          return (
            <div className="flex flex-col gap-1 w-full">
              <div className="flex items-center gap-1">
                {row?.isDuplicate && row?.status !== 'DRAFT' && (
                  <>
                    <div data-tooltip-id={`is-duplicate-${row.id}`}>
                      <Flag color={COLORS.WARNING_500} size={16} weight="fill" />
                    </div>
                    <ReactTooltip
                      id={`is-duplicate-${row.id}`}
                      place="top"
                      variant="dark"
                      opacity={'100%'}
                      style={{
                        display: 'flex',
                        background: '#334155',
                        width: '250px',
                        zIndex: 10000,
                        borderRadius: '8px',
                        alignItems: 'center',
                        justifyItems: 'center',
                        fontWeight: '600',
                      }}>
                      <span className="text-xs">
                        A credit application with this EIN already exists
                      </span>
                    </ReactTooltip>
                  </>
                )}
                <p className="text-xs capitalize w-full">
                  {firstName} {lastName}
                </p>
              </div>
              <div className="flex gap-1 items-center w-full">
                {row?.isFraudulent && (
                  <div data-tooltip-id={`is-fraud-${row.id}`}>
                    <Flag color={COLORS.ERROR_500} size={16} weight="fill" />
                  </div>
                )}
                <p className="text-xs w-full truncate">{row?.senderEmail || ''}</p>
                {row?.isFraudulent && (
                  <ReactTooltip
                    id={`is-fraud-${row.id}`}
                    place="top"
                    variant="dark"
                    opacity={'100%'}
                    float
                    style={{
                      display: 'flex',
                      background: '#334155',
                      width: '250px',
                      zIndex: 10000,
                      borderRadius: '8px',
                      alignItems: 'center',
                      justifyItems: 'center',
                      fontWeight: '600',
                    }}>
                    <span className="text-xs">
                      Email or website domain does not match the company&apos;s verified website
                    </span>
                  </ReactTooltip>
                )}
              </div>
              {!archiveCreditAppToogle && (
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full w-fit ${
                    row?.isArchived
                      ? 'ERROR_100-BG ERROR_500-CLR'
                      : 'PRIMARY_100-BG PRIMARY_500-CLR'
                  }`}>
                  {row?.isArchived ? 'Archived' : 'Active'}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'company',
        label: 'Customer/Company',
        width: 160,
        sortable: true,
        getSortValue: (row: CreditApplicationWithHistory) =>
          (
            row.fieldValues.find((f) => f.id === 'customer-or-company-name')?.value ?? ''
          ).toLowerCase(),
        getSearchValue: (row: CreditApplicationWithHistory) =>
          row.fieldValues.find((f) => f.id === 'customer-or-company-name')?.value ?? '',
        render: (_: any, row: CreditApplicationWithHistory) => {
          const companyName = row.fieldValues.find(
            (f) => f.id === 'customer-or-company-name'
          )?.value;
          const displayName = (companyName || '').toLowerCase();
          return (
            <p
              className="text-xs capitalize w-full"
              title={displayName}
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
              {displayName}
            </p>
          );
        },
      },
      {
        key: 'address',
        label: 'Address',
        width: 200,
        sortable: true,
        getSortValue: (row: CreditApplicationWithHistory) => {
          const fv = (id: string) => row.fieldValues.find((f) => f.id === id)?.value ?? '';
          return `${fv('company-address')} ${fv('company-city')} ${fv('company-state')} ${fv(
            'company-zip'
          )}`
            .trim()
            .toLowerCase();
        },
        getSearchValue: (row: CreditApplicationWithHistory) => {
          const fv = (id: string) => row.fieldValues.find((f) => f.id === id)?.value ?? '';
          return `${fv('company-address')} ${fv('company-address-2')} ${fv('company-city')} ${fv(
            'company-state'
          )} ${fv('company-zip')}`
            .replace(/\s+/g, ' ')
            .trim();
        },
        render: (_: any, row: CreditApplicationWithHistory) => {
          const fv = (id: string) => row.fieldValues.find((f) => f.id === id)?.value ?? '';
          const address = fv('company-address');
          const address2 = fv('company-address-2');
          const city = fv('company-city');
          const state = fv('company-state');
          const zip = fv('company-zip');
          const line1 = [address, address2].filter(Boolean).join(', ');
          const line2 = [[city, state].filter(Boolean).join(', '), zip].filter(Boolean).join(' ');
          if (!line1 && !line2) return <p className="text-xs GREY_400-CLR">—</p>;
          return (
            <div className="flex flex-col w-full">
              {line1 && <p className="text-xs capitalize w-full truncate">{line1.toLowerCase()}</p>}
              {line2 && (
                <p className="text-xs capitalize w-full truncate TEXT_SECONDARY-CLR">
                  {line2.toLowerCase()}
                </p>
              )}
            </div>
          );
        },
      },
      {
        key: 'createdAt',
        label: 'Created/Submitted At',
        width: 175,
        sortable: true,
        getSearchValue: (row: CreditApplicationWithHistory) => {
          const dateStr = row.createdAt ? moment(row.createdAt).format('MM/DD/YYYY') : '';
          return `${dateStr} ${row?.requestedBy ?? ''}`.trim();
        },
        render: (_: any, row: CreditApplicationWithHistory) => (
          <div className="flex flex-col">
            <p className="text-xs w-full">{moment(row.createdAt).format('MM/DD/YYYY')}</p>
            {row?.requestedBy && (
              <p className="text-xm capitalize w-full PRIMARY_500-CLR">
                {`Requested By: ${(row?.requestedBy || '').toLowerCase()}`}
              </p>
            )}
          </div>
        ),
      },
      {
        key: 'amount',
        label: 'Amount of Credit',
        width: 148,
        sortable: true,
        getSortValue: (row: CreditApplicationWithHistory) => {
          if (row.applicationType === 'CASH_APPLICATION') return 0;
          const raw = row.fieldValues.find((f) => f.id.includes('amount-of'))?.value ?? '';
          return isNaN(Number(raw)) ? 0 : Number(raw);
        },
        getSearchValue: (row: CreditApplicationWithHistory) => {
          const raw = row.fieldValues.find((f) => f.id.includes('amount-of'))?.value;
          const amount =
            row.applicationType === 'CASH_APPLICATION' ? 'COD' : raw ? formatMoney(raw, 0) : '';
          const approved = row.amountAproved ? formatMoney(row.amountAproved, 0) : '';
          const values = [amount, amount.replace(/,/g, ''), approved, approved.replace(/,/g, '')];
          return values.join(' ').trim();
        },
        render: (_: any, row: CreditApplicationWithHistory) => {
          const creditAmountRaw = row.fieldValues.find((f) => f.id.includes('amount-of'))?.value;
          let amount = creditAmountRaw ? formatMoney(creditAmountRaw, 0) : '';
          let amountApproved = row.amountAproved ? formatMoney(row.amountAproved, 0) : '';
          if (row.applicationType === 'CASH_APPLICATION') {
            amount = 'COD';
            amountApproved = '';
          }
          if (
            row.approvalStatusHistory &&
            row.approvalStatusHistory.length > 0 &&
            row.applicationType === 'CASH_APPLICATION'
          ) {
            const sortedHistory = [...row.approvalStatusHistory].sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
            );
            if (
              sortedHistory[0].status === 'APPROVED_COD' ||
              sortedHistory[0].status === 'INTERNALLY_APPROVED_COD'
            ) {
              amount = creditAmountRaw ? formatMoney(creditAmountRaw, 0) : '';
              amountApproved = 'COD';
            }
          }
          return (
            <div className="flex flex-col w-full">
              <p className="text-xs capitalize w-full text-right">{amount}</p>
              {amountApproved && row.status.includes('APPROVED') && (
                <p className="text-xs capitalize w-full PRIMARY_500-CLR text-right text-nowrap">{`Approved: ${amountApproved}`}</p>
              )}
            </div>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        width: 200,
        sortable: true,
        getSearchValue: (row: CreditApplicationWithHistory) => {
          const latestFeedback = getLatestFeedback(row);
          return [
            row.status ? row.status.replaceAll('_', ' ').toLowerCase() : '',
            ...(latestFeedback?.scope ?? []),
            latestFeedback ? FEEDBACK_STATUS_SEARCH_TERMS[latestFeedback.status] : '',
          ].join(' ');
        },
        render: (_: any, row: CreditApplicationWithHistory) => {
          const latestFeedback = getLatestFeedback(row);
          const latestFeedbackScope = latestFeedback?.scope ?? null;
          const tradeReference = row.fieldValues.filter((f) =>
            f.id.includes('trade-reference-company-email')
          );
          let tradeReferenceValue = '';
          if (
            tradeReference.length > 0 &&
            (row.status === 'SUBMITTED' ||
              row.status === 'AWAITING_SIGNATURE' ||
              row.status === 'INTERNAL_SIGNATURE')
          ) {
            const submittedRef = tradeReference.filter((f) => f.status === 'Submitted');
            tradeReferenceValue = `${submittedRef.length}/${tradeReference.length} Trade Reference${
              tradeReference.length > 1 ? 's' : ''
            } Returned`;
          }
          return (
            <div className="flex flex-col w-full min-w-0">
              <AppCreditApplicationStatus
                status={row.status}
                wrapperClassName="w-fit text-nowrap"
              />
              {row.status === 'DRAFT' && row?.currentStep && (
                <p className="text-xm capitalize w-full PRIMARY_500-CLR mt-0.5">
                  {`Step: ${row?.currentStep.stepNumber}/${row?.currentStep?.totalSteps ?? ''} - ${
                    row?.currentStep?.stepName ?? ''
                  }`}
                </p>
              )}
              {tradeReferenceValue && (
                <p className="text-xs capitalize w-full PRIMARY_500-CLR mt-[4px] w-full truncate">
                  {tradeReferenceValue}
                </p>
              )}
              {latestFeedback && latestFeedbackScope && (
                <>
                  {latestFeedback.status === 'RESOLVED' ? (
                    <div
                      data-tooltip-id={`feedback-scope-${row.id}`}
                      className="flex items-center gap-1 mt-[4px] w-full min-w-0 text-gray-300">
                      <ChatCircleText size={12} className="shrink-0" />
                      <span className="text-xs truncate">Feedback resolved</span>
                    </div>
                  ) : (
                    <div
                      data-tooltip-id={`feedback-scope-${row.id}`}
                      className="flex items-center gap-1 mt-[4px] w-full min-w-0">
                      <ChatCircleText
                        size={12}
                        className={`shrink-0 ${
                          latestFeedback.status === 'IN_REVIEW' ? 'text-blue-500' : 'text-gray-400'
                        }`}
                      />
                      <span
                        className={`text-xs shrink-0 ${
                          latestFeedback.status === 'IN_REVIEW' ? 'text-blue-600' : 'text-gray-500'
                        }`}>
                        {FEEDBACK_STATUS_LABELS[latestFeedback.status]}:
                      </span>
                      <span className="text-xs text-gray-700 truncate min-w-0">
                        {latestFeedbackScope[0] ?? 'Message only'}
                      </span>
                      {latestFeedbackScope.length > 1 && (
                        <span className="shrink-0 rounded-full bg-gray-100 px-1.5 text-[10px] font-medium text-gray-600">
                          +{latestFeedbackScope.length - 1}
                        </span>
                      )}
                    </div>
                  )}
                  <ReactTooltip
                    id={`feedback-scope-${row.id}`}
                    place="top"
                    variant="dark"
                    opacity={'100%'}
                    style={{
                      background: '#334155',
                      maxWidth: '260px',
                      zIndex: 10000,
                      borderRadius: '8px',
                    }}>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold">
                        {latestFeedback.status === 'IN_REVIEW'
                          ? 'Customer updated the requested fields. Needs review.'
                          : latestFeedback.status === 'RESOLVED'
                          ? 'All feedback resolved'
                          : 'Feedback sent. Awaiting customer.'}
                      </span>
                      {latestFeedback.openCount > 1 && (
                        <span className="text-xs text-gray-300">
                          {latestFeedback.openCount} open feedback requests
                        </span>
                      )}
                      {latestFeedback?.message && (
                        <p className="text-xs italic whitespace-pre-line break-words">
                          &ldquo;{latestFeedback.message}&rdquo;
                        </p>
                      )}
                      {latestFeedbackScope.length > 0 && (
                        <span className="text-xs font-semibold mt-1">Flagged fields</span>
                      )}
                      {latestFeedbackScope.length > 0 ? (
                        <ul className="list-disc pl-4 text-xs">
                          {latestFeedbackScope.map((name) => (
                            <li key={name}>{name}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-xs text-gray-300">No fields flagged</span>
                      )}
                    </div>
                  </ReactTooltip>
                </>
              )}
            </div>
          );
        },
      },
      {
        key: 'ballInCourt',
        label: 'Ball in Court',
        width: 170,
        sortable: true,
        getSortValue: (row: CreditApplicationWithHistory) =>
          companyUsers.find((u) => u.id === row.ballInCourtId)?.name ?? '',
        getSearchValue: (row: CreditApplicationWithHistory) =>
          companyUsers.find((u) => u.id === row.ballInCourtId)?.name ?? '',
        render: (_: any, row: CreditApplicationWithHistory) =>
          renderBallInCourt(row.ballInCourtId, (userId) => handleUpdateBallInCourt(row.id, userId)),
      },
      {
        key: 'branch',
        label: 'Branch',
        width: 170,
        sortable: true,
        getSortValue: (row: CreditApplicationWithHistory) =>
          supplierBranches.find((b) => b.branchId === row.branchId)?.branchName ?? '',
        getSearchValue: (row: CreditApplicationWithHistory) => {
          const branch = supplierBranches.find((b) => b.branchId === row.branchId);
          const salesReps = supplierBranches.flatMap((b) => b?.salesRep ?? []) ?? [];
          const repNames = salesReps
            .filter((sr) => row?.assignedSalesRep?.includes(sr?.id ?? ''))
            .map((sr) => sr.name)
            .join(' ');
          return `${branch?.branchName ?? ''} ${branch?.erpId ?? ''} ${repNames}`.trim();
        },
        render: (_: any, row: CreditApplicationWithHistory) => {
          const selectedBranch = supplierBranches.find((b) => b.branchId === row.branchId);
          const salesReps = supplierBranches.flatMap((b) => b?.salesRep ?? []) ?? [];
          const selectedSalesReps: string[] = Array.from(
            new Set(
              salesReps
                .filter((sr) => row?.assignedSalesRep?.includes(sr?.id ?? ''))
                .map((sr) => sr.name)
                .filter((name): name is string => !!name)
            )
          );
          const firstRep = selectedSalesReps[0];
          const extraCount = selectedSalesReps.length - 1;
          return (
            <div className="w-full flex flex-col gap-1">
              <button
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg border bg-white hover:bg-gray-50 transition-colors text-left ${
                  selectedBranch ? 'border-gray-200' : 'border-dashed border-gray-300'
                } ${!canAssignBranch ? 'cursor-not-allowed' : ''}`}
                disabled={!canAssignBranch}
                onClick={() => {
                  setCurrentCreditAppId(row.id);
                  setAssignBranchModalOpen(true);
                }}>
                <MapPin
                  size={13}
                  color={selectedBranch ? '#3b82f6' : '#9ca3af'}
                  weight="fill"
                  className="shrink-0"
                />
                <span className="text-xs truncate flex-1">
                  {selectedBranch ? (
                    <span className="text-gray-800">{formatBranchLabel(selectedBranch)}</span>
                  ) : (
                    <span className="text-gray-400">Assign branch & rep</span>
                  )}
                </span>
              </button>
              {firstRep && (
                <SalesRepsTooltip reps={selectedSalesReps}>
                  {({ onMouseEnter, onMouseLeave, ref }) => (
                    <div
                      ref={ref as React.RefObject<HTMLDivElement>}
                      onMouseEnter={onMouseEnter}
                      onMouseLeave={onMouseLeave}
                      className="flex items-center gap-1 cursor-default min-w-0">
                      <div className="h-5 w-5 rounded-full bg-blue-100 text-blue-600 text-[9px] font-semibold flex items-center justify-center shrink-0">
                        {firstRep.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-[11px] text-gray-600 truncate">{firstRep}</span>
                      {extraCount > 0 && (
                        <span className="text-[10px] text-gray-400 shrink-0">+{extraCount}</span>
                      )}
                    </div>
                  )}
                </SalesRepsTooltip>
              )}
            </div>
          );
        },
      },
      {
        key: 'notes',
        label: 'Notes',
        width: 70,
        sortable: false,
        render: (_: any, row: CreditApplicationWithHistory) => {
          const isSalesRole = user?.roles?.includes('CREDIT_APP_SALES') ?? false;
          const notes = row.internalNotes ?? [];
          // Credit sees every note; Sales sees notes shared with them plus their own.
          const visibleNotes = isSalesRole
            ? notes.filter((n) => n.visibleToSales || n.fromSales)
            : notes;
          return (
            <button
              className="relative flex items-center justify-center w-[35px] h-[35px] rounded hover:bg-gray-100"
              onClick={() => {
                setNotesModalCreditApp(row);
                setNotesModalOpen(true);
              }}
              data-tooltip-id={`notes-indicator-${row.id}`}>
              <ChatText size={20} color={COLORS.GREY_400} />
              {visibleNotes.length > 0 && (
                <span className="absolute -top-1 -right-1 PRIMARY_500-BG text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {visibleNotes.length}
                </span>
              )}
              <ReactTooltip
                id={`notes-indicator-${row.id}`}
                place="bottom"
                variant="dark"
                opacity={'100%'}
                style={{
                  background: '#334155',
                  zIndex: 10000,
                  borderRadius: '8px',
                  fontWeight: '600',
                }}>
                <span>Internal Notes</span>
              </ReactTooltip>
            </button>
          );
        },
      },
      {
        key: 'approvedAt',
        label: 'Approved At',
        width: 130,
        sortable: true,
        getSortValue: (row: CreditApplicationWithHistory) =>
          row.approvedAt ? new Date(row.approvedAt).getTime() : 0,
        render: (_: any, row: CreditApplicationWithHistory) =>
          row.approvedAt ? (
            <p className="text-xs w-full">{moment(row.approvedAt).format('MM/DD/YYYY')}</p>
          ) : (
            <p className="text-xs GREY_400-CLR">—</p>
          ),
      },
      {
        key: 'action',
        label: '',
        width: 210,
        sortable: false,
        stickyRight: true,
        render: (_: any, row: CreditApplicationWithHistory) => {
          const isDraftOrPending =
            row.status === 'SENT' || row.status === 'OPENED' || row.status === 'DRAFT';
          const archiveOption = {
            label: row.isArchived ? 'Unarchive' : 'Archive',
            value: row.isArchived ? ('unarchive' as const) : ('archive' as const),
            icon: row.isArchived ? (
              <TrayArrowUp color={COLORS.GREY_500} size={20} />
            ) : (
              <TrayArrowDown color={COLORS.GREY_500} size={20} />
            ),
          };
          const dropdownOptions = [
            ...(isDraftOrPending
              ? [
                  {
                    label: 'Change Requirements',
                    value: 'change-requirements',
                    icon: <SlidersHorizontal color={COLORS.GREY_500} size={20} />,
                  },
                ]
              : []),
            ...(isDraftOrPending
              ? []
              : [
                  {
                    label: 'Preview',
                    value: 'preview',
                    icon: <Eye color={COLORS.GREY_500} size={20} />,
                  },
                  {
                    label: 'Download',
                    value: 'download',
                    icon: <DownloadSimple color={COLORS.GREY_500} size={20} />,
                  },
                  ...(row.status === 'APPROVED' || row.status === 'REJECTED'
                    ? [
                        {
                          label: 'Request Update',
                          value: 'request-update',
                          icon: (
                            <Pencil
                              color={row.templateId ? COLORS.GREY_500 : COLORS.GREY_300}
                              size={20}
                            />
                          ),
                          isDisabled: !row.templateId,
                          tooltipText: !row.templateId
                            ? 'Request update option is not available for applications not requested using Dill'
                            : undefined,
                        },
                      ]
                    : []),
                  ...(user && row.status === 'INTERNAL_SIGNATURE'
                    ? [
                        {
                          label: 'Resend Internal Signature',
                          value: 'resend-internal-signature',
                          icon: <PaperPlaneTilt color={COLORS.GREY_500} size={20} />,
                        },
                      ]
                    : []),
                ]),
            archiveOption,
            {
              label: 'Delete',
              value: 'delete',
              icon: <Trash color={COLORS.ERROR_500} size={20} />,
            },
          ];
          return (
            <div className="flex items-center justify-center gap-1.5 w-full">
              {isDraftOrPending ? (
                <div data-tooltip-id={`resend-credit-app-${row.id}`}>
                  <DillButton
                    variant="icon-default"
                    icon={<ArrowCounterClockwise size={20} color={COLORS.GREY_500} />}
                    onClick={() => {
                      setCurrentCreditAppId(row.id);
                      setCreditAppCommentModalOpen(true);
                    }}
                    height={34}
                    width={34}
                  />
                  <ReactTooltip
                    id={`resend-credit-app-${row.id}`}
                    place="bottom"
                    variant="dark"
                    opacity={'100%'}
                    style={{
                      display: 'flex',
                      background: '#334155',
                      width: '70px',
                      zIndex: 10000,
                      borderRadius: '8px',
                      alignItems: 'center',
                      justifyItems: 'center',
                      fontWeight: '600',
                    }}>
                    <span>Resend</span>
                  </ReactTooltip>
                </div>
              ) : (
                // Keeps the Review button aligned across rows with and without Resend.
                <div className="w-[34px] shrink-0" />
              )}
              {row.applicationUpdateHistory && row.applicationUpdateHistory.length > 0 ? (
                <DillDropdownButton
                  label="Review"
                  showChevron
                  isDisabled={user?.roles?.includes('CREDIT_APP_SALES')}
                  options={[
                    { label: 'Version 1 (Original)', value: '' },
                    ...[...row.applicationUpdateHistory]
                      .sort(
                        (a, b) =>
                          new Date(a.createdAt ?? 0).getTime() -
                          new Date(b.createdAt ?? 0).getTime()
                      )
                      .map((h, idx) => ({
                        label: `Version ${idx + 2}`,
                        value: h.id,
                      })),
                  ]}
                  onSelect={(opt) =>
                    navigate(
                      `/creditApplications/${row.id}${opt.value ? `?historyId=${opt.value}` : ''}`
                    )
                  }
                  width={100}
                />
              ) : (
                <DillButton
                  label="Review"
                  onClick={() =>
                    navigate(
                      buildSupplierPath(currentSupplier?.id, `/creditApplications/${row.id}`)
                    )
                  }
                  isDisabled={user?.roles?.includes('CREDIT_APP_SALES')}
                  width={100}
                />
              )}
              <DillDropdownButton
                variant="icon-default"
                options={dropdownOptions}
                onSelect={(val) => handleSelectValue(val.value, row.id)}
                isDisabled={previewLoading !== '' || user?.roles?.includes('CREDIT_APP_SALES')}
                isLoading={previewLoading === row.id}
                icon={<DotsThreeVertical size={18} />}
                height={34}
                width={34}
              />
            </div>
          );
        },
      },
    ],
    [
      archiveCreditAppToogle,
      previewLoading,
      companyUsers,
      supplierBranches,
      user,
      canAssignBranch,
      handleSelectValue,
      handleUpdateBallInCourt,
      navigate,
    ]
  );

  // Internal form submissions share this table (see CreditApplications.tsx); their rows get their
  // own cells, sort values and search values instead of the credit application ones.
  const tableColumns = useMemo<DillColumn<CreditApplicationWithHistory>[]>(
    () =>
      columns.map((column) => ({
        ...column,
        render: (value: any, row: CreditApplicationWithHistory, index: number) => {
          const submission = row.internalFormSubmission;
          // Notes work the same for internal forms (NotesModal saves them to the submission).
          if (!submission || column.key === 'notes') {
            return column.render ? column.render(value, row, index) : value;
          }
          if (column.key === 'ballInCourt') {
            return renderBallInCourt(submission.ballInCourtId, (userId) =>
              handleUpdateFormBallInCourt(submission.id, userId)
            );
          }
          // The branch assigned (e.g. when an external form was sent), else the sender's.
          const branch = supplierBranches.find(
            (b) => b.branchId === (submission.assignedBranchId || submission.branchId)
          );
          return getInternalFormCell({
            columnKey: column.key,
            submission,
            branchName: branch?.branchName ?? '',
            onReview: () =>
              navigate(
                buildSupplierPath(
                  currentSupplier?.id,
                  `/creditApplications/internalForms/${submission.id}`
                )
              ),
            onPdf: (type) => handlePreviewOrDownload(submission.id, type, true),
            onResend: () => handleResendInternalForm(submission.id),
            onDelete: user?.roles?.includes('CREDIT_APP_SALES')
              ? undefined
              : () => handleDeleteInternalForm(submission.id),
            isPdfLoading: previewLoading === submission.id,
            isPdfDisabled: previewLoading !== '',
          });
        },
        ...(column.getSortValue
          ? {
              getSortValue: (row: CreditApplicationWithHistory) =>
                row.internalFormSubmission
                  ? getInternalFormSortValue(column.key, row.internalFormSubmission)
                  : column.getSortValue!(row),
            }
          : {}),
        ...(column.getSearchValue
          ? {
              getSearchValue: (row: CreditApplicationWithHistory) =>
                row.internalFormSubmission
                  ? getInternalFormSearchValue(column.key, row.internalFormSubmission)
                  : column.getSearchValue!(row),
            }
          : {}),
      })),
    [
      columns,
      supplierBranches,
      navigate,
      currentSupplier?.id,
      previewLoading,
      handlePreviewOrDownload,
      user,
    ]
  );

  return (
    <div className="w-full flex flex-col">
      <CreditAppCommentModal
        open={creditAppCommentModalOpen}
        handleClose={() => {
          setCreditAppCommentModalOpen(false);
          setCurrentCreditAppId('');
        }}
        title="Resend Link"
        type="resend-draft"
        creditApplicationId={currentCreditAppId}
      />
      <RequestApplicationUpdateModal
        open={openRequestUpdateModal}
        handleClose={() => {
          setOpenRequestUpdateModal(false);
          setCurrentCreditAppId('');
        }}
        email={selectedSenderEmail}
        creditApplicationId={currentCreditAppId}
      />
      <AppPdfPreviewModal
        open={previewModalOpen}
        handleClose={() => setPreviewModalOpen(false)}
        invoice={''}
        pdfType={'CREDIT_APPLICATION'}
        pdfList={[{ url: currentPdfUrl, date: new Date(), name: '' }]}
        handleDownloadPdf={() => {}}
        mode="Supplier"
      />
      {currentCreditAppId && (
        <AssignBranchModal
          open={assignBranchModalOpen}
          handleClose={() => {
            setAssignBranchModalOpen(false);
            setCurrentCreditAppId('');
          }}
          creditApplicationId={currentCreditAppId}
          reload={true}
          allowedBranchIds={assignBranchAllowedIds}
          lockBranch={!!tmHierarchy && user?.roles?.includes('CREDIT_APP_SALES')}
        />
      )}
      <DillTable
        columns={tableColumns}
        data={creditApplications}
        summaryCards={summaryCards}
        toolbarLeft={toolbarLeft}
        toolbarRight={toolbarRight}
        sortable
        pagination
        pageSize={20}
        emptyMessage="No credit applications."
        striped={false}
        rowHeight={72}
        loadingText="Loading application…"
        countLabel="Applications"
        showCount={false}
        scrollX
        searchClassName="h-[34px]"
        strictColumnSearch
      />
      {notesModalOpen && notesModalCreditApp && (
        <NotesModal
          creditApp={notesModalCreditApp}
          isSalesRole={user?.roles?.includes('CREDIT_APP_SALES') ?? false}
          onClose={() => setNotesModalOpen(false)}
          onInternalFormUpdated={onInternalFormUpdated}
        />
      )}
      <ChangeDraftRequirementsModal
        open={changeDraftRequirementsModalOpen}
        handleClose={() => {
          setChangeDraftRequirementsModalOpen(false);
          setChangeDraftRequirementsCreditApp(null);
        }}
        creditApplication={changeDraftRequirementsCreditApp}
      />
      {currentCreditAppId && (
        <SendInternalSignatureDocumentsModal
          open={resendInternalSignatureModalOpen}
          handleClose={() => {
            setResendInternalSignatureModalOpen(false);
            setCurrentCreditAppId('');
          }}
          creditApplicationId={currentCreditAppId}
          onSuccess={() =>
            dispatch(getCreditApplications({ supplierId: currentSupplier?.id ?? '' }))
          }
        />
      )}
    </div>
  );
};

export default CreditApplicationsTable;
